<template>
  <div
      class="app-checkbox"
      :class="{
        invalid: hasError,
        light,
      }"
  >
    <label>
      <div>
        <input
            type="checkbox"
            :checked="modelValue"
            @input="emitInput"
        />
        <span>
          <icon-check/>
        </span>
      </div>
      <div class="fs-regular">
        <slot></slot>
        <div
            v-if="hasError"
            class="io-shop-input--message fs-small"
            v-html="errorMessage"
        ></div>
      </div>
    </label>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, InputHTMLAttributes,
} from 'vue';

import IconCheck from '@/components/inputs/IconCheck.vue';

const modelValue = defineModel();
const error = defineModel<undefined | string | string[]>('error');

defineProps<{
  light?: boolean
}>();

const hasError = computed(() => (error.value ? error.value.length > 0 : false));

const errorMessage = computed(() => {
  if (hasError.value) {
    return Array.isArray(error.value)
      ? error.value[0]
      : error.value;
  }

  return '';
});

const value = computed({
  get(): unknown {
    return modelValue.value;
  },
  set(newValue: unknown): void {
    modelValue.value = newValue;
  },
});

const emitInput = (event: Event) => {
  value.value = (event.target as InputHTMLAttributes | undefined)?.checked;
};
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

@mixin input-border() {
  border: 2px solid #DBDBDB;
  border-radius: 5px;
}

@mixin row {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex !important;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
}

@mixin col {
  -ms-flex-preferred-size: 0;
  flex-basis: 0;
  -webkit-box-flex: 1;
  -ms-flex-positive: 1;
  flex-grow: 1;
  max-width: 100%;
}

@mixin col-auto {
  -webkit-box-flex: 0;
  -ms-flex: 0 0 auto;
  flex: 0 0 auto;
  width: auto;
  max-width: none;
}

@mixin col-full {
  -webkit-box-flex: 0;
  -ms-flex: 0 0 100%;
  flex: 0 0 100%;
  max-width: 100%;
}

.app-checkbox {
  position: relative;
  --app-checkbox-color: #38383A;

  &.light {
    --app-checkbox-color: #FFFFFF;
  }

  > label {
    &:first-child {
      @include row;
      align-items: center;

      > div {
        &:first-child {
          @include col-auto;
          align-self: start;
          padding-top: .25em;

          input {
            opacity: 0;
            visibility: hidden;
            position: absolute;
            pointer-events: none;

            + span {
              display: block;
              background: #FFFFFF 0% 0% no-repeat padding-box;
              box-shadow: 0px 2px 4px #00000029;
              border: 1px solid #E7F1F5;
              border-radius: 5px;
              position: relative;
              @include responsive-size-sm(20);
              height: 1.5em;
              width: 1.5em;

              > svg {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translateY(-50%) translateX(-50%);

                * {
                  stroke: #FFFFFF;
                }
              }
            }

            &:checked {
              + span {
                > svg * {
                  stroke: var(--color-brand-primary);
                }
              }
            }
          }
        }

        &:last-child {
          @include col;
          @include font-weight-fix;
          line-height: 1.6;
          color: var(--app-checkbox-color);
          padding: 0 .5em;
        }
      }
    }
  }

  &.invalid {
    > label {
      > div {
        &:first-child {
          input {
            + span {
              border-color: var(--color-brand-primary);;
            }
          }
        }
      }
    }

    .io-shop-input--message {
      font-weight: 400;
      color: #b61c0f;
      font-size: 10px;
    }
  }

  a {
    color: var(--color-brand-primary);
    text-decoration: none;

    &:hover {
      color: var(--color-brand-primary-hover);
    }
  }
}
</style>
